import {
  FeaturesNames,
  PackageOfServices,
  PermissionAndService,
  PermissionGroup,
  ServicesList,
} from '@/shared/types/settings'

const SKIPPED_FEATURE_NAMES = new Set<string>([
  FeaturesNames.CALL_WAITING,
  FeaturesNames.CALL_INTRUSION,
])

const isPickupGroup = (group: PermissionGroup) => group.type === 'pickup'

const isSkippedFeatureName = (name: string) => SKIPPED_FEATURE_NAMES.has(name)

const getActivePackages = (packages: PackageOfServices[]) =>
  packages.filter((item) => item.enabled)

const checkIsInPackage = (serviceName: string, packages: PackageOfServices[]) =>
  packages.some((item) => item.groupNames.includes(serviceName))

const getPackageNamesForService = (serviceName: string, packages: PackageOfServices[]) => {
  const names = new Set<string>()
  packages.forEach((item) => {
    if (item.groupNames.includes(serviceName)) names.add(item.name)
  })
  return names
}

const toRowFromGroup = (
  group: PermissionGroup,
  dstResult: string,
  packages: Set<string>,
  isInPackage: boolean,
): PermissionAndService => ({
  ...group,
  packages,
  dstResult,
  isInPackage,
})

const toRowFromService = (
  service: ServicesList,
  packages: Set<string>,
  isInPackage: boolean,
): PermissionAndService => ({
  aclEnabled: false,
  guid: service.guid,
  name: service.name,
  type: '',
  packages,
  group: '',
  dstResult: service.dstResult,
  isInPackage,
})

const toPackageOnlyRow = (
  name: string,
  packageNames: Set<string>,
): PermissionAndService => ({
  aclEnabled: false,
  guid: '',
  name,
  type: '',
  packages: packageNames,
  group: '',
  dstResult: '',
  isInPackage: true,
})

export const buildServicesAndPermissionList = (
  services: ServicesList[],
  groups: PermissionGroup[],
  packages: PackageOfServices[],
): Map<string, PermissionAndService> => {
  const activePackages = getActivePackages(packages)
  const result = new Map<string, PermissionAndService>()

  const groupsByName = new Map<string, PermissionGroup>()
  groups.forEach((group) => {
    if (isPickupGroup(group) || isSkippedFeatureName(group.name)) return
    groupsByName.set(group.name, group)
  })

  const servicesByName = new Map<string, ServicesList>()
  services.forEach((service) => {
    if (isSkippedFeatureName(service.name)) return
    servicesByName.set(service.name, service)
  })

  servicesByName.forEach((service) => {
    const group = groupsByName.get(service.name)
    const inPackage = checkIsInPackage(service.name, activePackages)
    const packageNames = getPackageNamesForService(service.name, activePackages)

    if (group) {
      result.set(
        service.name,
        toRowFromGroup(
          group,
          service.dstResult,
          inPackage ? packageNames : new Set<string>(),
          inPackage,
        ),
      )
      return
    }

    result.set(service.name, toRowFromService(service, packageNames, inPackage))
  })

  groupsByName.forEach((group) => {
    if (result.has(group.name)) return

    const inPackage = checkIsInPackage(group.name, activePackages)
    const packageNames = getPackageNamesForService(group.name, activePackages)

    result.set(
      group.name,
      toRowFromGroup(
        group,
        '',
        inPackage ? packageNames : new Set<string>(),
        inPackage,
      ),
    )
  })

  if (
    !result.has(FeaturesNames.ACCESS_GROUP_MANAGEMENT) &&
    checkIsInPackage(FeaturesNames.ACCESS_GROUP_MANAGEMENT, activePackages)
  ) {
    result.set(
      FeaturesNames.ACCESS_GROUP_MANAGEMENT,
      toPackageOnlyRow(
        FeaturesNames.ACCESS_GROUP_MANAGEMENT,
        getPackageNamesForService(FeaturesNames.ACCESS_GROUP_MANAGEMENT, activePackages),
      ),
    )
  }

  return result
}
